import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Wand2, Loader2, KeyRound, Copy, UserCheck } from 'lucide-react';
import { useActionBarConfig } from '../../contexts/ActionBarContext';
import api from '@/lib/axios';
import { footerToParams, getFooterSettings } from '@/components/FooterDateBar';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/utils';
import RecordNavigator from '@/components/ui/record-navigator';
import SearchBuilder, { applyFilters } from '@/components/search/SearchBuilder'
import useTableColumns from '@/hooks/useTableColumns';
import useTableSort from '@/hooks/useTableSort';
import SortableTh from '@/components/table/SortableTh';
import useListKeyboardNav from '@/hooks/useListKeyboardNav';
import PhoneLink from '@/components/PhoneLink';
import BulkAppKennungen from '@/components/app/BulkAppKennungen';

const TEACHER_SORT_ACCESSORS = {
  name: (t) => `${t.last_name || ''} ${t.first_name || ''}`.trim(),
  instruments: (t) => (t.instruments || []).join(', '),
  email: (t) => t.email,
  phone: (t) => t.phone,
  status: (t) => t.status,
  created_at: (t) => t.created_at,
};

// Define filterable fields for teachers
const FILTER_FIELDS = [
  { key: 'first_name', label: 'Vorname', type: 'text' },
  { key: 'last_name', label: 'Nachname', type: 'text' },
  { key: 'email', label: 'E-Mail', type: 'text' },
  { key: 'phone', label: 'Telefon', type: 'text' },
  { key: 'status', label: 'Status', type: 'select', options: [
    { value: 'active', label: 'Aktiv' },
    { value: 'inactive', label: 'Inaktiv' },
  ]},
];

// Define columns with visibility
const DEFAULT_COLUMNS = [
  { key: 'name', label: 'Name', visible: true },
  { key: 'instruments', label: 'Instrumente', visible: true },
  { key: 'email', label: 'E-Mail', visible: true },
  { key: 'phone', label: 'Telefon', visible: true },
  { key: 'status', label: 'Status', visible: true },
  { key: 'created_at', label: 'Erstellt am', visible: true },
];

export default function TeachersList() {
  const [allTeachers, setAllTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([]);
  const { columns, setColumns } = useTableColumns('teachers-list', DEFAULT_COLUMNS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTeacher, setNewTeacher] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [accountResult, setAccountResult] = useState(null);
  const navigate = useNavigate();
  const [footerTick, setFooterTick] = useState(0);
  useEffect(() => {
    const onFooter = () => setFooterTick((n) => n + 1);
    window.addEventListener('footer-settings-changed', onFooter);
    return () => window.removeEventListener('footer-settings-changed', onFooter);
  }, []);

  const createTeacher = async () => {
    if (!newTeacher.first_name.trim() || !newTeacher.last_name.trim() || !newTeacher.email.trim()) {
      toast.error('Vorname, Nachname und E-Mail sind Pflichtfelder');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/teachers', {
        first_name: newTeacher.first_name.trim(),
        last_name: newTeacher.last_name.trim(),
        email: newTeacher.email.trim(),
        phone: newTeacher.phone.trim() || null,
      });
      setShowCreateDialog(false);
      setNewTeacher({ first_name: '', last_name: '', email: '', phone: '' });
      toast.success(`Lehrkraft ${res.data.first_name} ${res.data.last_name} angelegt`);
      setAccountResult(res.data.user_account || null);
      await loadTeachers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Anlegen der Lehrkraft');
    } finally {
      setCreating(false);
    }
  };

  const copyCredentials = () => {
    navigator.clipboard.writeText(
      `Benutzername: ${accountResult.username}\nPasswort: ${accountResult.initial_password}`
    );
    toast.success('Zugangsdaten kopiert');
  };

  useEffect(() => {
    loadTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerTick]);

  const loadTeachers = async () => {
    setLoading(true);
    try {
      // Load in batches since API has limit of 100
      let allData = [];
      let offset = 0;
      const batchSize = 100;
      const period = footerToParams(getFooterSettings());
      
      while (true) {
        const response = await api.get('/teachers', {
          params: { limit: batchSize, offset, ...period },
        });
        const batch = response.data;
        if (!batch || batch.length === 0) break;
        allData = [...allData, ...batch];
        if (batch.length < batchSize) break;
        offset += batchSize;
      }
      
      setAllTeachers(allData);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error loading teachers:', error);
      toast.error('Fehler beim Laden der Lehrer');
    } finally {
      setLoading(false);
    }
  };

  const { sort, toggleSort, sortRows } = useTableSort(TEACHER_SORT_ACCESSORS, { key: 'name' });

  // Apply filters client-side for instant feedback
  const filteredTeachers = useMemo(() => {
    const searchFields = ['first_name', 'last_name', 'email', 'phone'];
    return sortRows(applyFilters(allTeachers, filters, searchTerm, searchFields));
  }, [allTeachers, filters, searchTerm, sortRows]);

  const { getRowProps } = useListKeyboardNav({
    items: filteredTeachers,
    onSelect: (t) => navigate(`/teachers/${t.id}`),
  });

  useActionBarConfig({
    list: { kind: 'teacher', ids: filteredTeachers.map((t) => t.id) },
    onNew: () => setShowCreateDialog(true),
    newLabel: 'Neue Lehrkraft anlegen',
  }, [filteredTeachers]);

  const unassignedCount = allTeachers.filter(t => !t.instruments || t.instruments.length === 0).length;

  const autoAssignInstruments = async () => {
    const overwrite = window.confirm(
      `Möchten Sie die Instrumente automatisch aus den vorhandenen Kursen ableiten?\n\n` +
      `OK = nur ${unassignedCount} unzugewiesene Lehrer befüllen\n` +
      `Abbrechen = alle Lehrer überschreiben (auch bereits zugewiesene)`
    );
    // window.confirm: OK = true (skip already assigned), Cancel = false (overwrite)
    setAutoAssigning(true);
    try {
      const res = await api.post(`/teachers/auto-assign-instruments?overwrite=${!overwrite}`);
      const { updated, skipped, no_courses } = res.data;
      toast.success(
        `${updated} Lehrer aktualisiert · ${skipped} übersprungen · ${no_courses} ohne Kurse`,
        { duration: 5000 }
      );
      await loadTeachers();
    } catch (e) {
      toast.error('Auto-Zuordnung fehlgeschlagen');
    } finally {
      setAutoAssigning(false);
    }
  };

  const handleNavigate = (newIndex) => {
    setCurrentIndex(newIndex);
    const row = document.querySelector(`[data-testid="teacher-row-${filteredTeachers[newIndex]?.id}"]`);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.add('bg-blue-50');
      setTimeout(() => row.classList.remove('bg-blue-50'), 2000);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { label: 'Aktiv', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      inactive: { label: 'Inaktiv', className: 'bg-slate-100 text-slate-700' },
    };
    const config = variants[status] || variants.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const isColumnVisible = (key) => {
    const col = columns.find(c => c.key === key);
    return col ? col.visible : true;
  };

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1" data-testid="teachers-title">
              Lehrer
            </h1>
            <p className="text-slate-600">
              {filteredTeachers.length} von {allTeachers.length} Lehrern
            </p>
          </div>
          <div className="flex items-center gap-2">
            <BulkAppKennungen personType="teacher" audienceLabel="Lehrer" />
            {unassignedCount > 0 && (
              <Button
                onClick={autoAssignInstruments}
                disabled={autoAssigning}
                variant="outline"
                data-testid="auto-assign-instruments-btn"
                className="border-brand-red/20 text-brand-red hover:bg-brand-red/5"
              >
                {autoAssigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                {unassignedCount} auto-zuordnen
              </Button>
            )}
            <Button
              onClick={() => setShowCreateDialog(true)}
              data-testid="add-teacher-button"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neuer Lehrer
            </Button>
          </div>
        </div>

        {/* SearchBuilder */}
        <SearchBuilder
          fields={FILTER_FIELDS}
          onFilterChange={setFilters}
          onSearch={setSearchTerm}
          columns={columns}
          onColumnsChange={setColumns}
          placeholder="Lehrer suchen (Name, E-Mail, Telefon)..."
          className="mb-4"
        />

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-600">
              {filteredTeachers.length} Ergebnisse
            </span>
            
            {filteredTeachers.length > 0 && (
              <RecordNavigator
                currentIndex={currentIndex}
                totalCount={filteredTeachers.length}
                onNavigate={handleNavigate}
                records={filteredTeachers}
                getRecordLabel={(teacher) => `${teacher.last_name}, ${teacher.first_name}`}
              />
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="teachers-table">
              <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider border-b border-slate-200">
                <tr>
                  {DEFAULT_COLUMNS.filter((col) => isColumnVisible(col.key)).map((col) => (
                    <SortableTh
                      key={col.key}
                      sortKey={col.key}
                      sort={sort}
                      onSort={toggleSort}
                      testIdPrefix="teachers-sort"
                    >
                      {col.label}
                    </SortableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-slate-500">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredTeachers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-slate-500">
                      Keine Lehrer gefunden
                    </td>
                  </tr>
                ) : (
                  filteredTeachers.map((teacher, idx) => (
                    <tr
                      key={teacher.id}
                      data-testid={`teacher-row-${teacher.id}`}
                      className="border-b border-slate-100 hover:bg-brand-red/5 transition-colors cursor-pointer data-[keyboard-selected=true]:bg-brand-red/10 data-[keyboard-selected=true]:ring-2 data-[keyboard-selected=true]:ring-brand-red/30 data-[keyboard-selected=true]:ring-inset"
                      {...getRowProps(idx, teacher)}
                    >
                      {isColumnVisible('name') && (
                        <td className="p-4 text-sm text-slate-700 font-medium">
                          {teacher.first_name} {teacher.last_name}
                        </td>
                      )}
                      {isColumnVisible('instruments') && (
                        <td className="p-4 text-sm text-slate-600">
                          {teacher.instruments && teacher.instruments.length > 0 ? (
                            <div className="flex flex-wrap gap-1" title={teacher.instruments.join(', ')}>
                              {teacher.instruments.slice(0, 3).map((inst) => (
                                <span key={inst} className="inline-block px-2 py-0.5 rounded-full bg-brand-red/5 text-brand-red text-xs font-medium">
                                  {inst}
                                </span>
                              ))}
                              {teacher.instruments.length > 3 && (
                                <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                                  +{teacher.instruments.length - 3}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-brand-red text-xs italic">— nicht zugewiesen</span>
                          )}
                        </td>
                      )}
                      {isColumnVisible('email') && (
                        <td className="p-4 text-sm text-slate-600">{teacher.email || '-'}</td>
                      )}
                      {isColumnVisible('phone') && (
                        <td className="p-4 text-sm text-slate-600" onClick={(e) => e.stopPropagation()}>
                          {teacher.phone ? <PhoneLink phone={teacher.phone} data-testid={`teacher-phone-${teacher.id}`} /> : '-'}
                        </td>
                      )}
                      {isColumnVisible('status') && (
                        <td className="p-4 text-sm">{getStatusBadge(teacher.status)}</td>
                      )}
                      {isColumnVisible('created_at') && (
                        <td className="p-4 text-sm text-slate-600">{formatDate(teacher.created_at)}</td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Neue Lehrkraft anlegen */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md" data-testid="create-teacher-dialog">
          <DialogHeader>
            <DialogTitle>Neue Lehrkraft anlegen</DialogTitle>
            <DialogDescription>
              Es wird automatisch ein Benutzerkonto mit der Rolle „Lehrer" erstellt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="teacher-first-name">Vorname *</Label>
                <Input
                  id="teacher-first-name"
                  value={newTeacher.first_name}
                  onChange={(e) => setNewTeacher((p) => ({ ...p, first_name: e.target.value }))}
                  data-testid="teacher-first-name-input"
                />
              </div>
              <div>
                <Label htmlFor="teacher-last-name">Nachname *</Label>
                <Input
                  id="teacher-last-name"
                  value={newTeacher.last_name}
                  onChange={(e) => setNewTeacher((p) => ({ ...p, last_name: e.target.value }))}
                  data-testid="teacher-last-name-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="teacher-email">E-Mail * (wird Benutzername)</Label>
              <Input
                id="teacher-email"
                type="email"
                value={newTeacher.email}
                onChange={(e) => setNewTeacher((p) => ({ ...p, email: e.target.value }))}
                data-testid="teacher-email-input"
              />
            </div>
            <div>
              <Label htmlFor="teacher-phone">Telefon</Label>
              <Input
                id="teacher-phone"
                value={newTeacher.phone}
                onChange={(e) => setNewTeacher((p) => ({ ...p, phone: e.target.value }))}
                data-testid="teacher-phone-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Abbrechen</Button>
            <Button
              onClick={createTeacher}
              disabled={creating}
              data-testid="create-teacher-submit"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zugangsdaten anzeigen (einmalig) */}
      <Dialog open={!!accountResult} onOpenChange={(open) => !open && setAccountResult(null)}>
        <DialogContent className="max-w-md" data-testid="teacher-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {accountResult?.created ? (
                <><KeyRound className="w-5 h-5 text-emerald-600" /> Benutzerkonto erstellt</>
              ) : (
                <><UserCheck className="w-5 h-5 text-brand-red" /> Benutzerkonto</>
              )}
            </DialogTitle>
            <DialogDescription>
              {accountResult?.created
                ? 'Bitte notieren Sie das Start-Passwort — es wird nur einmal angezeigt. Die Lehrkraft muss es beim ersten Login ändern.'
                : accountResult?.reason || 'Kein Konto erstellt.'}
            </DialogDescription>
          </DialogHeader>
          {accountResult?.created && (
            <div className="space-y-2 p-4 bg-slate-50 rounded-lg font-mono text-sm">
              <div data-testid="teacher-credentials-username">
                <span className="text-slate-500">Benutzername: </span>{accountResult.username}
              </div>
              <div data-testid="teacher-credentials-password">
                <span className="text-slate-500">Passwort: </span>{accountResult.initial_password}
              </div>
            </div>
          )}
          {accountResult?.created && (
            <div
              className={`text-xs px-3 py-2 rounded-lg ${accountResult.email_sent
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'}`}
              data-testid="teacher-credentials-email-status"
            >
              {accountResult.email_sent
                ? `Zugangsdaten wurden per E-Mail an ${accountResult.username} gesendet.`
                : `E-Mail-Versand nicht möglich: ${accountResult.email_message || 'Unbekannter Fehler'}. Bitte Zugangsdaten manuell weitergeben.`}
            </div>
          )}
          <DialogFooter>
            {accountResult?.created && (
              <Button variant="outline" onClick={copyCredentials} data-testid="copy-credentials-btn">
                <Copy className="w-4 h-4 mr-2" /> Kopieren
              </Button>
            )}
            <Button onClick={() => setAccountResult(null)} className="bg-slate-900 text-white hover:bg-slate-800">
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
